import type {RawAudience} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {MetricInsightsPopupMenuItem} from './components/metric-insights/metric-insights.types';

export interface RecipientOpenInsight {
  idEmailMarketingCampaign: string;
  email: string;
  name: string;
}

enum MetricType {
  METRIC_TYPE_CLICK = 'click',
  METRIC_TYPE_OPEN = 'open',
  METRIC_TYPE_DELIVERY = 'delivery',
}

enum MetricItemType {
  CAMPAIGN = 'c',
  AUDIENCE = 'a',
}

export interface Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY | MetricType.METRIC_TYPE_OPEN | MetricType.METRIC_TYPE_CLICK;
  positiveOutcomeLabel: string;
  negativeOutcomeLabel: string;
  positiveOutcomePercentage: number;
  negativeOutcomePercentage: number;
  positiveOutcomeTotal: number;
  negativeOutcomeTotal: number;
  total: number;
}

export interface DeliveryMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY;
  negativeOutcomeDetails: {
    label: string;
    value: number;
  }[];
}

export interface OpenMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_OPEN;
}

export interface ClickMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY;
}

export interface CampaignClickInsights {
  links: {name: string}[];
  showMoreLabel: string;
}

export interface CampaignMetricDetails {
  idCampaign: string;
  idMailingList: number;
  email: string;
  isDelivered: boolean;
  isBounce: boolean;
  isUnsubscribe: boolean;
  isComplaint: boolean;
  isMarkedAsSpam: boolean;
  isInvalid: boolean;
  isDeleted: boolean;
  numOpens: number;
  numClicks: number;
  clickInsights: CampaignClickInsights;
  openInsights: RecipientOpenInsight[];
}

export interface SingleCampaignMetrics {
  idCampaign: string;
  delivery: DeliveryMetrics;
  open: OpenMetrics;
  click: ClickMetrics;
  details: Record<string, CampaignMetricDetails>;
  score: number;
}

export enum RecipientInsightsSortField {
  EMAIL = 'email',
  DELIVERED = 'delivered',
  OPENS = 'opens',
  CLICKS = 'clicks',
  UNSUBSCRIBED = 'unsubscribed',
}

export enum RecipientInsightsSortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum RecipientInsightsFilter {
  INVALID = 'invalid',
}

export const RECIPIENT_INSIGHTS_DEFAULT_PAGE_LIMIT = 25;
export const INVALID_RECIPIENTS_FETCH_LIMIT = 1000;

export interface SingleMailingListMetricsOverview {
  numRecipients: number;
  numDelivered: number;
  numOpens: number;
  numClicks: number;
  numBounces: number;
  numUnsubscribes: number;
  numComplaints: number;
  score?: number;
}

export interface EmailMarketingCampaign {
  id: string;
  name: string;
  isDraft: boolean;
  isScheduled: boolean;
  publishOn: string;
  isSharedInTeam: boolean;
  idOwner: number;
}

export interface OptimalTimeResponse {
  maximumUtcTimestamp: number;
  minimumUtcTimestamp: number;
}

export interface MaxOptimalTimePayload {
  idEmailMarketingCampaign: string;
  clientTimezoneOffset: number;
  scheduledDate?: string;
}

export interface UserMailingList {
  id: number;
  name: string;
  createdOn: string;
  numEmails?: number;
  lastViewed?: string;
  numNonDeletedEmails?: number;
  isActive?: boolean;
  validEmailsCount?: number;
  lastUpdatedOn?: string;
  mailingListContacts?: RawAudience[];
  isSmart: boolean;
  isSpam: boolean;
  numNewContacts?: number;
  isSharedInTeam: boolean;
  idOwner: number;
}

export interface MailingListMetricsInsight {
  email: string;
  numClicks: number;
  numOpens: number;
  opens: MetricInsightsPopupMenuItem[];
  links: MetricInsightsPopupMenuItem[];
  isUnsubscribe: boolean;
  isBounce: boolean;
  isDeleted: boolean;
}

export interface MailingListMetrics {
  idMailingList: number;
  numRecipients: number;
  numDelivered: number;
  numOpens: number;
  numClicks: number;
  numBounces: number;
  numUnsubscribes: number;
  numComplaints: number;
  insights: Record<string, MailingListMetricsInsight>;
  score: number;
}

export interface CampaignPerformance {
  performanceLoadingState: number;
  selectedItemIds: string[];
  isCollectingData: boolean;
  itemType: MetricItemType.CAMPAIGN | MetricItemType.AUDIENCE;
}

export interface EmailMarketingCampaignWithMailingLists extends CampaignPerformance {
  itemType: MetricItemType.AUDIENCE;
  mailingLists: Record<string, UserMailingList>;
  mailingListsWithAvailableMetrics: Record<string, number>;
  mailingListsWithMetricsNotAvailable: Record<string, number>;
  mailingListsWithMetricsCollecting: Record<string, number>;
  mailingListMetrics: Record<string, MailingListMetrics>;
  isNoMailingListCreated?: boolean;
  isNotCollectingData?: boolean;
}

export interface EmailMarketingCampaignWithCampaigns extends CampaignPerformance {
  itemType: MetricItemType.CAMPAIGN;
  campaigns: Record<string, EmailMarketingCampaign>;
  campaignsWithMetricsAvailable: Record<string, string>;
  campaignsWithMetricsNotAvailable: Record<string, string>;
  campaignsWithMetricsCollecting: Record<string, string>;
  campaignMetrics: Record<string, SingleCampaignMetrics>;
  isNoCampaignCreated?: boolean;
  isNoCampaignPublished?: boolean;
}

export const DEFAULT_ITEM_ID = 'none';
export const COMPARISON_VIEW_SCREEN_WIDTH_BREAKPOINT_PX = 1000;

export interface RecipientInsightsPaging {
  offset: number;
  limit: number;
  sortField: RecipientInsightsSortField;
  sortDir: RecipientInsightsSortDirection;
}

export interface EmailMarketingCampaignPerformanceState {
  performanceLoadingState: number;
  campaigns: Record<string, EmailMarketingCampaign>;
  campaignsWithMetricsAvailable: Record<string, string>;
  campaignsWithMetricsNotAvailable: Record<string, string>;
  campaignsWithMetricsCollecting: Record<string, string>;
  mailingLists: Record<string, UserMailingList>;
  mailingListsWithAvailableMetrics: Record<string, number>;
  mailingListsWithMetricsNotAvailable: Record<string, number>;
  mailingListsWithMetricsCollecting: Record<string, number>;
  campaignMetrics: Record<string, SingleCampaignMetrics>;
  mailingListMetrics: Record<string, MailingListMetrics>;
  selectedItemIds: string[];
  itemType: string;
  isNoCampaignCreated: boolean;
  isNoCampaignPublished: boolean;
  isCollectingData: boolean;
  isNoMailingListCreated: boolean;
  isNotCollectingData: boolean;
  detailsLoadingState: number;
  detailsTotalRows: number;
  detailsRequestedPaging: RecipientInsightsPaging;
  detailsLoadedRowsPaging: RecipientInsightsPaging;
  detailsLatestRequestId: string;
}

export interface FetchCampaignRecipientInsightsArg {
  campaignId: string;
  limit: number;
  offset: number;
  sortField: RecipientInsightsSortField;
  sortDir: RecipientInsightsSortDirection;
}

export interface FetchInvalidCampaignRecipientEmailsArg {
  campaignId: string;
  offset: number;
}

export interface FetchCampaignRecipientInsightsResponse {
  details: Record<string, CampaignMetricDetails>;
  totalRows: number | null;
  limit: number;
  offset: number;
}

export interface FetchInvalidCampaignRecipientEmailsResponse extends Omit<FetchCampaignRecipientInsightsResponse, 'totalRows'> {
  totalRows: number;
}

export interface InitEmailMarketingCampaignPerformanceArg {
  itemType: string;
  itemIds: string[];
}

export type InitEmailMarketingCampaignPerformanceResponse = Pick<
  EmailMarketingCampaignPerformanceState,
  | 'campaigns' | 'campaignsWithMetricsAvailable' | 'campaignsWithMetricsNotAvailable'
  | 'campaignsWithMetricsCollecting' | 'campaignMetrics' | 'mailingLists'
  | 'mailingListsWithAvailableMetrics' | 'mailingListsWithMetricsNotAvailable'
  | 'mailingListsWithMetricsCollecting' | 'mailingListMetrics' | 'itemType'
  | 'isNoCampaignCreated' | 'isNoCampaignPublished'
  | 'isCollectingData' | 'isNoMailingListCreated' | 'isNotCollectingData'
> & {
  selectedItemIds: (string | number)[];
};

export interface RemoveInvalidEmailsArg {
  id: string;
  emails: string[];
}

export interface RemoveAllInvalidCampaignEmailsArg {
  id: string;
  excludedEmails: string[];
}

export interface PerformanceInputs {
  itemType: string;
  selectedItemIds: string[];
}
